import React from 'react';
import PropTypes from 'prop-types';
import gql from 'graphql-tag';
import { useMutation } from '@apollo/react-hooks';
import { withRouter } from 'react-router-dom';
import { injectIntl, defineMessages } from 'react-intl';
import { withFormik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { withGrowl } from '../../components/GrowlNotifications';
import { intlShape, historyShape, growlShape } from '../../components/prop-types';
import Debug from '../../components/Debug';
import { validationMessages, notificationMessages } from '../../i18n/common';
import { DropdownWithValidation } from '../../components/prime-formik';

const getUserRoleOptions = (userRole) => {
  const memberOption = {
    label: 'Member',
    value: 'MEMBER',
  };

  const adminOption = {
    label: 'Admin',
    value: 'ADMIN',
  };

  const externalSupportOption = {
    label: 'External Support',
    value: 'EXTERNAL_SUPPORT',
  };

  const userRoleToCompare = userRole.toUpperCase();
  const userRoleOptions = [];
  if (userRoleToCompare === 'MEMBER') {
    userRoleOptions.push(adminOption);
    userRoleOptions.push(externalSupportOption);
  }

  if (userRoleToCompare === 'ADMIN') {
    userRoleOptions.push(memberOption);
    userRoleOptions.push(externalSupportOption);
  }

  return userRoleOptions;
};

const messages = defineMessages({
  availableRoles: { id: 'profile.available-roles', defaultMessage: 'Available roles' },
  member: { id: 'profile.member-role', defaultMessage: 'Member' },
  admin: { id: 'profile.admin-role', defaultMessage: 'Administrator' },
  externalSupport: { id: 'profile.external-support-role', defaultMessage: 'External Support' },
  cancel: { id: 'actions.cancel', defaultMessage: 'Cancel' },
  submit: { id: 'actions.submit', defaultMessage: 'Submit' },
  updateUserRole: {
    id: 'users.update-role.heading',
    defaultMessage: 'Update user role',
  },
});

const UPDATE_USER_ROLE = gql`
  mutation updateUserRole($input: UpdateUserRoleForAccountInput!) {
    updateUserRoleForAccount(input: $input) {
        clientMutationId
    }
  }
`;

function UpdateUserRoleForm(props) {
  const {
    accountId,
    userId,
    userRole,
    intl: { formatMessage },
    toggleEdit,
  } = props;

  const userRoleOptions = getUserRoleOptions(userRole);
  return (
    <div className="p-grid">
      <div className="p-sm-12 p-md-6">
        <Form>
          <input type="hidden" name="account" value={accountId} />
          <input type="hidden" name="user" value={userId} />
          <div className="p-grid">
            <div className="p-col-12" style={{ width: '3.5em' }}>
              <Field
                id="role"
                name="role"
                labelText={formatMessage(messages.availableRoles)}
                component={DropdownWithValidation}
                options={userRoleOptions}
              />
            </div>
          </div>
          <div className="p-grid">
            <div className="p-col-12">
              <Button
                type="button"
                className="p-button-secondary"
                label={formatMessage(messages.cancel)}
                onClick={() => toggleEdit(false)}
              />
              <Button type="submit" label={formatMessage(messages.submit)} />
            </div>
          </div>
          <Debug />
        </Form>
      </div>
    </div>
  );
}

UpdateUserRoleForm.propTypes = {
  accountId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  userRole: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  toggleEdit: PropTypes.func.isRequired,
};

async function handleSubmit(values, { props, setSubmitting }) {
  const {
    updateUserRoleMutation,
    growl,
    intl: { formatMessage },
    toggleEdit,
  } = props;

  const {
    account,
    user,
    role,
  } = values;

  try {
    await updateUserRoleMutation({
      variables: {
        input: {
          account,
          user,
          role,
        },
      },
    });
    setSubmitting(false);
    growl.show({ severity: 'info', detail: formatMessage(notificationMessages.success), summary: 'Info Message' });
    toggleEdit(false);
  } catch (error) {
    growl.show({
      severity: 'error', detail: error.message, sticky: true,
    });
  }
}

function buildValidationSchema(formatMessage) {
  return Yup.object().shape({
    account: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    user: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
    role: Yup.string()
      .required(formatMessage(validationMessages.required))
      .default(''),
  });
}

function mapPropsToValues({ intl: { formatMessage }, accountId, userId }) {
  // Formik requires initial values for each field, to support error messages on submit
  const validationSchema = buildValidationSchema(formatMessage);

  const initialValues = {
    ...validationSchema.cast(),
    account: accountId,
    user: userId,
  };
  return initialValues;
}

const UpdateUserRoleFormEnhancedForm = withFormik({
  validationSchema: ({ intl: { formatMessage } }) => buildValidationSchema(formatMessage),
  mapPropsToValues,
  handleSubmit,
})(UpdateUserRoleForm);

function UserDetailEdit(props) {
  const {
    intl: { formatMessage },
  } = props;

  const [updateUserRoleMutation] = useMutation(UPDATE_USER_ROLE, {
    refetchQueries: ['getUserById'],
  });

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <Panel header={formatMessage(messages.updateUserRole)}>
          <UpdateUserRoleFormEnhancedForm
            updateUserRoleMutation={updateUserRoleMutation}
            {...props}
          />
        </Panel>
      </div>
    </div>
  );
}

UserDetailEdit.propTypes = {
  accountId: PropTypes.string.isRequired,
  userId: PropTypes.string.isRequired,
  intl: intlShape.isRequired,
  history: historyShape.isRequired,
  growl: growlShape.isRequired,
};

export default injectIntl(withRouter((withGrowl(UserDetailEdit))));
